import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  CalendarClock, MoreHorizontal, Phone, MessageSquare, MapPin, Pencil,
  ExternalLink, Plus, Navigation,
} from "lucide-react";
import { fmtMoney, fmtTime, STATUS_STYLES, statusLabel, SectionCard } from "./dashUtils";

const TABS = [
  { id: "upcoming", label: "Upcoming" },
  { id: "in_progress", label: "In Progress" },
  { id: "attention", label: "Needs Attention" },
  { id: "completed", label: "Completed" },
];

const JobRow = ({ job, onOpen, financialAccess }) => {
  const navigate = useNavigate();
  const time = job.scheduled_time
    ? `${fmtTime(job.scheduled_time)}${job.scheduled_time_end ? ` – ${fmtTime(job.scheduled_time_end)}` : ""}`
    : "";
  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-border/70 px-3 py-2.5 hover:border-blue-500/40 hover:bg-blue-500/[0.03] transition-colors cursor-pointer"
      onClick={() => onOpen(job)}
      data-testid={`ops-job-${job.job_id}`}
    >
      <div className="w-[74px] shrink-0">
        {time ? (
          <p className="text-xs font-semibold text-foreground leading-tight">{time}</p>
        ) : (
          <p className="text-[11px] text-muted-foreground">{(job.scheduled_date || "").slice(0, 10) || "—"}</p>
        )}
        <Badge className={`${STATUS_STYLES[job.status] || "bg-gray-500/10 text-gray-500"} border-0 text-[9px] px-1.5 mt-1 capitalize`}>
          {statusLabel(job.status)}
        </Badge>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{job.title}</p>
        <p className="text-xs text-muted-foreground truncate">
          {job.customer_name}
          {job.address ? <span className="text-muted-foreground/60"> · {job.address}</span> : null}
        </p>
        <p className="text-[11px] text-muted-foreground/70 truncate flex items-center gap-2 mt-0.5">
          {job.location_name && <span className="inline-flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{job.location_name}</span>}
          {job.technician ? <span>Tech: {job.technician}</span> : <span className="text-amber-600 font-medium">Unassigned</span>}
        </p>
      </div>
      {financialAccess && (job.customer_total > 0 || job.paid > 0) && (
        <div className="text-right shrink-0 hidden sm:block">
          <p className="text-sm font-semibold text-foreground">{fmtMoney(job.customer_total)}</p>
          {job.balance > 0 && job.paid > 0 ? (
            <p className="text-[10px] text-orange-500">Paid {fmtMoney(job.paid)} · Bal {fmtMoney(job.balance)}</p>
          ) : job.balance > 0 ? (
            <p className="text-[10px] text-muted-foreground">Balance {fmtMoney(job.balance)}</p>
          ) : (
            <p className="text-[10px] text-emerald-600">Paid</p>
          )}
        </div>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground" data-testid={`ops-actions-${job.job_id}`}>
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem onClick={() => onOpen(job)} data-testid="ops-open-job"><ExternalLink className="w-3.5 h-3.5 mr-2" /> Open Job</DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate(`/dashboard/jobs/${job.job_id}/edit`)}><Pencil className="w-3.5 h-3.5 mr-2" /> Edit Job</DropdownMenuItem>
          {job.customer_phone && (
            <DropdownMenuItem onClick={() => { window.location.href = `tel:${job.customer_phone}`; }}>
              <Phone className="w-3.5 h-3.5 mr-2" /> Call Customer
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => navigate("/dashboard/messages")}><MessageSquare className="w-3.5 h-3.5 mr-2" /> Message Customer</DropdownMenuItem>
          {job.address && (
            <DropdownMenuItem onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.address)}`, "_blank")}>
              <Navigation className="w-3.5 h-3.5 mr-2" /> Directions
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const TodaysOperations = ({ operations, onOpenJob, financialAccess }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("upcoming");
  const ops = operations || {};
  const rows = ops[tab] || [];
  return (
    <SectionCard title="Today's Operations" icon={CalendarClock} testid="todays-operations"
      action={
        <Button size="sm" variant="ghost" className="h-7 text-xs text-blue-500 hover:text-blue-600" onClick={() => navigate("/dashboard/jobs")}>
          All Jobs
        </Button>
      }>
      <div className="flex gap-1 mb-3 overflow-x-auto">
        {TABS.map((t) => {
          const count = (ops[t.id] || []).length;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                tab === t.id ? "bg-blue-600 text-white" : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
              }`}
              data-testid={`ops-tab-${t.id}`}
            >
              {t.label}{count > 0 && <span className={`ml-1 ${tab === t.id ? "opacity-80" : "opacity-60"}`}>{count}</span>}
            </button>
          );
        })}
      </div>
      {rows.length === 0 ? (
        <div className="text-center py-8" data-testid="ops-empty">
          <p className="text-sm text-muted-foreground mb-3">
            {tab === "upcoming" ? "No jobs scheduled today" : tab === "in_progress" ? "No jobs in progress" : tab === "attention" ? "No jobs need attention" : "No completed jobs in this period"}
          </p>
          {tab === "upcoming" && (
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => navigate("/dashboard/jobs/new")} data-testid="ops-create-job">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Create Job
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
          {rows.map((j) => <JobRow key={`${j.job_id}`} job={j} onOpen={onOpenJob} financialAccess={financialAccess} />)}
        </div>
      )}
    </SectionCard>
  );
};

export default TodaysOperations;
